import { z } from 'zod';

import { zId, zNonEmptyString, zOptionalISODate, zOptionalString } from './common';

/* ─── Типы документов ───────────────────────────────────────────────────── */

export const DOCUMENT_TYPES = [
  'CONTRACT',
  'PASSPORT',
  'VISA',
  'INSURANCE',
  'MEDICAL',
  'INVOICE',
  'OTHER',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/**
 * Типы документов, которые содержат личные данные человека, а не дела
 * организации. Скан паспорта — не то же самое, что счёт за буткемп: его видит
 * не всякий, кому позволено вести список людей. Доступ к ним закрыт правом
 * `people.sensitive.view` — тем же, что закрывает паспорт и реквизиты
 * в карточке, чтобы не появилось двух разных ответов на один вопрос.
 */
export const SENSITIVE_DOCUMENT_TYPES = ['PASSPORT', 'VISA', 'MEDICAL', 'INSURANCE'] as const;

export function isSensitiveDocument(type: string): boolean {
  return (SENSITIVE_DOCUMENT_TYPES as readonly string[]).includes(type);
}

/** Код системной папки. Их нельзя переименовать и удалить. */
export const SYSTEM_FOLDER_CODES = ['CONTRACTS'] as const;
export type SystemFolderCode = (typeof SYSTEM_FOLDER_CODES)[number];

/* ─── Ввод ──────────────────────────────────────────────────────────────── */

export const createFolderSchema = z.object({
  name: zNonEmptyString(80),
  parentId: zId.nullish(),
});
export type CreateFolderInput = z.infer<typeof createFolderSchema>;

export const updateFolderSchema = z.object({
  name: zNonEmptyString(80).optional(),
  /** Перенос в другую папку; null — в корень. */
  parentId: zId.nullish(),
});
export type UpdateFolderInput = z.infer<typeof updateFolderSchema>;

/**
 * Поля документа при загрузке.
 *
 * Приходят вместе с файлом в multipart, поэтому числа и даты — строки:
 * форма не умеет присылать типы, и приводим их здесь.
 */
export const uploadDocumentSchema = z.object({
  title: zOptionalString(160),
  type: z.enum(DOCUMENT_TYPES).default('OTHER'),
  number: zOptionalString(60),
  issuedAt: zOptionalISODate,
  expiresAt: zOptionalISODate,
  reminderDaysBefore: z.coerce.number().int().min(0).max(365).default(30),
  notes: zOptionalString(500),
  folderId: zId.nullish(),
  personId: zId.nullish(),
});
export type UploadDocumentInput = z.infer<typeof uploadDocumentSchema>;

export const updateDocumentSchema = z.object({
  title: zNonEmptyString(160).optional(),
  type: z.enum(DOCUMENT_TYPES).optional(),
  number: zOptionalString(60),
  issuedAt: zOptionalISODate,
  expiresAt: zOptionalISODate,
  reminderDaysBefore: z.coerce.number().int().min(0).max(365).optional(),
  notes: zOptionalString(500),
  folderId: zId.nullish(),
  personId: zId.nullish(),
});
export type UpdateDocumentInput = z.infer<typeof updateDocumentSchema>;

export const documentFilterSchema = z.object({
  folderId: zId.optional(),
  /** Поиск идёт по всему складу, а не по текущей папке: иначе он бесполезен. */
  search: z.string().trim().max(120).optional(),
  personId: zId.optional(),
  type: z.enum(DOCUMENT_TYPES).optional(),
  expiringOnly: z.coerce.boolean().default(false),
});
export type DocumentFilter = z.infer<typeof documentFilterSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface DocumentFolderDto {
  id: string;
  name: string;
  parentId: string | null;
  code: string | null;
  isSystem: boolean;
  /** Сколько документов лежит прямо в этой папке. */
  documentCount: number;
  childCount: number;
  createdAt: string;
}

export interface DocumentDto {
  id: string;
  title: string;
  type: DocumentType;
  number: string | null;
  issuedAt: string | null;
  expiresAt: string | null;
  reminderDaysBefore: number;
  notes: string | null;

  fileName: string;
  mimeType: string;
  sizeBytes: number;
  isImage: boolean;

  folderId: string | null;
  folderName: string | null;
  personId: string | null;
  personNickname: string | null;

  /** Просрочен или истекает в ближайшие reminderDaysBefore дней. */
  isExpiring: boolean;
  isExpired: boolean;

  createdAt: string;
}

export interface DocumentBrowseResponse {
  /** Путь от корня до текущей папки — для хлебных крошек. */
  breadcrumbs: { id: string; name: string }[];
  folders: DocumentFolderDto[];
  documents: DocumentDto[];
  /** Сводка по всему складу, а не по текущей папке. */
  totals: { documents: number; sizeBytes: number; expiring: number };
}
